/**
 * Session Redux Slice — Multi-brand active-org state
 *
 * WHY THIS EXISTS
 * ---------------
 * Micas (the first tri-org tenant on shape-1) has one `micas_admin` user
 * that holds `user_organization_grants` to two consumer-facing brand orgs
 * (MICAS LAMINATES + MIKANO LAMINATES). On login the admin lands on the
 * parent-container org (which has no customer data), and a chip in the
 * shell header switches the "active brand" — the switch translates to an
 * `X-Active-Org-Id` header that the backend tenant-context middleware
 * already honors (Stage A / PR #30). See `MULTI_BRAND_DESIGN.md` §4.4 for
 * the full contract.
 *
 * WHY `activeOrgIds` IS A SET, NOT A SCALAR
 * -----------------------------------------
 * Even though today's UI only lets a user pick one brand at a time, we
 * model this as a `number[]`. Reason: `Smartass_Client.md` Q4 flagged a
 * future "unified list across both brands with a Brand column filter" as
 * a plausible Q4 pivot. Keeping the state as an array preserves the
 * escape hatch without a schema change — the interceptor emits a
 * comma-list when there's more than one value.
 *
 * The backend middleware today reads a single-value header, so the
 * interceptor only emits a real header when `activeOrgIds.length === 1`.
 * The comma-list code path is defensive-only until the future PR wires
 * the multi-select UI.
 *
 * PERSISTENCE
 * -----------
 * `activeOrgIds` is persisted to `localStorage` under the exact key
 * `activeOrgIds` (JSON-stringified array). The interceptor reads from
 * localStorage directly — that gives us a clean single source of truth
 * that doesn't require injecting the Redux store into the axios module.
 * `orgGrants` is NOT persisted — it's re-fetched on every authenticated
 * boot to catch grant revocations without a hard logout.
 *
 * INVARIANTS
 * ----------
 * - Empty `activeOrgIds` = no header stamped (no-op for every non-Micas
 *   admin, which is every tenant today).
 * - `orgGrants` starts empty and only fills after the boot-time fetch
 *   resolves — again a no-op for tenants without grants.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Storage key — DO NOT rename. Called out in the design doc and matched
// by the interceptor. Mobile has its own key (Stage D), we're web-only.
export const ACTIVE_ORG_IDS_STORAGE_KEY = 'activeOrgIds';

export interface OrgGrant {
    organizationId: number;
    organizationName: string;
    isActive: boolean;
}

export interface SessionState {
    activeOrgIds: number[];
    orgGrants: OrgGrant[];
    grantsLoaded: boolean;
    grantsLoading: boolean;
    grantsError: string | null;
}

const rehydrateActiveOrgIds = (): number[] => {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    try {
        const raw = window.localStorage.getItem(ACTIVE_ORG_IDS_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Only accept finite numbers — reject strings / nulls / NaN silently
        // so a corrupted key can never break the interceptor downstream.
        return parsed.filter((v) => typeof v === 'number' && Number.isFinite(v));
    } catch {
        return [];
    }
};

const persistActiveOrgIds = (ids: number[]): void => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(ACTIVE_ORG_IDS_STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // localStorage full / disabled — the interceptor will see stale
        // state but no header will crash. Log-silently on purpose.
    }
};

export const buildInitialSessionState = (): SessionState => ({
    activeOrgIds: rehydrateActiveOrgIds(),
    orgGrants: [],
    grantsLoaded: false,
    grantsLoading: false,
    grantsError: null,
});

const sessionSlice = createSlice({
    name: 'session',
    initialState: buildInitialSessionState(),
    reducers: {
        setActiveOrgIds(state, action: PayloadAction<number[]>) {
            // Coerce to unique finite numbers so bad callers can't wedge
            // the interceptor with strings or duplicates.
            const cleaned = Array.from(
                new Set(
                    (action.payload || []).filter(
                        (v) => typeof v === 'number' && Number.isFinite(v)
                    )
                )
            );
            state.activeOrgIds = cleaned;
            persistActiveOrgIds(cleaned);
        },
        clearActiveOrgIds(state) {
            state.activeOrgIds = [];
            persistActiveOrgIds([]);
        },
        setOrgGrants(state, action: PayloadAction<OrgGrant[]>) {
            state.orgGrants = action.payload || [];
            state.grantsLoaded = true;
            state.grantsLoading = false;
            state.grantsError = null;
        },
        grantsFetchStart(state) {
            state.grantsLoading = true;
            state.grantsError = null;
        },
        grantsFetchError(state, action: PayloadAction<string>) {
            state.grantsLoading = false;
            state.grantsError = action.payload;
            state.grantsLoaded = true; // "loaded" = "fetch attempted"
        },
        resetSession(state) {
            state.activeOrgIds = [];
            state.orgGrants = [];
            state.grantsLoaded = false;
            state.grantsLoading = false;
            state.grantsError = null;
            persistActiveOrgIds([]);
        },
    },
});

export const {
    setActiveOrgIds,
    clearActiveOrgIds,
    setOrgGrants,
    grantsFetchStart,
    grantsFetchError,
    resetSession,
} = sessionSlice.actions;

export default sessionSlice.reducer;
